import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import { Lock as LockOutlined, Headset as SupportAgentOutlined, User as PersonOutlined } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { TicketMessage } from '../../types/support';
import { useTicketMessages } from '../../hooks/useSupport';

interface Props {
  ticketId: string;
  currentUserId?: number;
}

export default function TicketMessageThread({ ticketId, currentUserId }: Props) {
  const { data: rawMessages = [], isLoading, isError } = useTicketMessages(ticketId);
  // Handle both flat array and paginated {results:[]} response shapes
  const messages: TicketMessage[] = Array.isArray(rawMessages) ? rawMessages : (rawMessages as any)?.results ?? [];
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography level="body-sm" sx={{ color: 'danger.600' }}>
          Unable to load messages. Please try refreshing.
        </Typography>
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          No messages yet. Be the first to reply.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {messages.map(msg => {
        const isSupport = msg.sender?.is_superuser;
        const isCurrentUser = msg.sender?.id === currentUserId;
        const alignRight = isCurrentUser && !isSupport;
        const senderName = msg.sender
          ? `${msg.sender.first_name} ${msg.sender.last_name}`.trim() || msg.sender.email
          : 'Unknown';

        return (
          <Stack
            key={msg.id}
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'flex-start', flexDirection: alignRight ? 'row-reverse' : 'row' }}
          >
            <Avatar size="sm" sx={{ bgcolor: isSupport ? 'primary.500' : 'neutral.300', flexShrink: 0 }}>
              {isSupport ? <SupportAgentOutlined size={16} /> : <PersonOutlined size={16} />}
            </Avatar>

            <Stack sx={{ maxWidth: '80%', alignItems: alignRight ? 'flex-end' : 'flex-start' }} spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                  {isSupport ? 'ApexOS Support' : senderName}
                </Typography>
                {msg.is_internal_note && (
                  <Chip size="sm" color="warning" variant="soft" startDecorator={<LockOutlined size={10} />}>
                    Internal Note
                  </Chip>
                )}
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                </Typography>
              </Stack>

              <Sheet
                variant={alignRight ? 'solid' : 'soft'}
                color={msg.is_internal_note ? 'warning' : alignRight ? 'primary' : 'neutral'}
                sx={{
                  p: 1.5,
                  borderRadius: alignRight ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                }}
              >
                {alignRight ? (
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', color: 'white' }}>
                    {msg.body}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      '& p': { m: 0, mb: 0.75 },
                      '& p:last-child': { mb: 0 },
                      '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5 },
                      '& li': { mb: 0.25 },
                      '& strong': { fontWeight: 700 },
                      '& h1, & h2, & h3': { fontSize: '0.95rem', fontWeight: 700, mt: 1, mb: 0.5 },
                    }}
                  >
                    <ReactMarkdown>{msg.body}</ReactMarkdown>
                  </Box>
                )}
              </Sheet>
            </Stack>
          </Stack>
        );
      })}
      <div ref={bottomRef} />
    </Stack>
  );
}
